import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import numeral from 'numeral'
import { useSelector } from 'react-redux'
import { Tooltip } from 'react-tooltip'
import cn from 'classnames'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'

import styles from './styles.module.scss'

import {
  deletePlaceExpense, getExpensesCompanyStats, getPlaceExpenses, getCompanyExpenseTypes,
  getCurrencies, getCustomers, downloadExpenseFile, getSuppliers, createSupplier,
  getMaterials, createMaterial
} from '../../../api'
import usePermissions from '../../../components/Core/usePermissions'
import { generateExpensesExcelExport } from '../../../helpers/expense_export'

import Button from '../../../components/Core/Button/Button'
import CreateExpense from '../../../components/Core/Dialog/CreateExpense'
import SearchIcon from '../../../components/Icons/SearchIcon'
import Input from '../../../components/Core/Input/Input'
import DollarIcon from '../../../components/Icons/DollarIcon'
import InvoiceIcon from '../../../components/Icons/InvoiceIcon'
import PlaceIcon from '../../../components/Icons/Place'
import ChartIcon from '../../../components/Icons/ChartIcon'
import DownloadIcon from '../../../components/Icons/DownloadIcon'
import EditIcon from '../../../components/Icons/EditIcon'
import TrashIcon from '../../../components/Icons/TrashIcon'
import DRP from '../../../components/Core/DRP/DRP'
import ConfirmationModal from '../../../components/Core/Dialog/ConfirmationModal'
import DescriptionIcon from '../../../components/Icons/DescriptionIcon'
import InputSelectMultiple from '../../../components/Core/InputSelectMultiple'
import InvoiceStatus from '../../Invoices/InvoiceStatus'
import Table from '../../Core/Table'

const EyeIcon = ({color = '#7B8794'}) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 5C7.09 5 2.91 8.05 1 12.35C2.91 16.65 7.09 19.7 12 19.7C16.91 19.7 21.09 16.65 23 12.35C21.09 8.05 16.91 5 12 5ZM12 17.2C9.33 17.2 7.16 15.03 7.16 12.35C7.16 9.68 9.33 7.5 12 7.5C14.67 7.5 16.84 9.68 16.84 12.35C16.84 15.03 14.67 17.2 12 17.2ZM12 9.44C10.4 9.44 9.09 10.75 9.09 12.35C9.09 13.96 10.4 15.26 12 15.26C13.6 15.26 14.91 13.96 14.91 12.35C14.91 10.75 13.6 9.44 12 9.44Z"
      fill={color}
    />
  </svg>
)

const ExcelIcon = (props) => {
  return (
    <svg focusable="false" viewBox="0 0 24.929 23.601" aria-hidden="true">
      <defs>
        <radialGradient id="radial-gradient" cx="6.02" cy="25.157" r="0.39" gradientTransform="translate(-3.837 -22.366) scale(1.661 1.921)" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor="#237f4c"></stop>
          <stop offset="1" stopColor="#1e6c41"></stop>
        </radialGradient>
      </defs>
      <rect id="rect4556" width="23.35" height="18.313" rx="3.375" transform="translate(1.23 2.582)" fill="#fff"></rect>
      <g id="use3985" transform="translate(13.449 2.344)">
        <path id="rect3991" d="M103.389-46.454h3.983v2.046h-3.983Zm4.8,0h3.983v2.042h-3.983Zm-4.8-2.966h3.983v2.144h-3.983Zm4.8,0h3.983v2.144h-3.983Zm-4.8-2.961h3.983v2.144h-3.983Zm4.8,0h3.983v2.144h-3.983Zm-4.8-2.961h3.983V-53.2h-3.983Zm4.8,0h3.983V-53.2h-3.983Zm-4.8-2.961h3.983v2.144h-3.983Zm4.8,0h3.983v2.144h-3.983Zm-4.938-2.447v.813h10.658v17.262H103.25v.817h10.59a.889.889,0,0,0,.889-.889V-59.862a.889.889,0,0,0-.889-.889H103.25Z" transform="translate(-103.25 60.751)" fill="#1f7144"></path>
      </g>
      <path id="path3945" d="M18.68,9.063,4.469,11.59V30.1l14.2,2.566Zm-4.544,7.616L12.1,20.861l2.089,4.208L12.3,24.958l-1.242-3.08-1.217,2.94-1.664-.094,1.953-3.863L8.277,16.955l1.8-.085,1.047,2.846,1.259-2.957Z" transform="translate(-4.469 -9.063)" fill="url(#radial-gradient)"></path>
    </svg>
  )
}

const columnHelper = createColumnHelper()

const formatExpenseAmount = (amount, currencyCode) => {
  const numericAmount = Number(amount || 0)

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericAmount)
  } catch (error) {
    return `${currencyCode || ''} ${numeral(numericAmount).format('0,0.00')}`.trim()
  }
}

const formatExpenseDate = (date, format) => {
  if (!date) {
    return '-'
  }

  const parsedDate = moment(date)
  return parsedDate.isValid() ? parsedDate.format(format) : '-'
}

const capitalize = value => {
  if (!value) {
    return '-'
  }

  const text = String(value).replaceAll('_', ' ')

  return text.charAt(0).toUpperCase() + text.slice(1)
}

const getBillingStatusText = (status, dueDate) => {
  if (!dueDate) {
    return status
  }
  if (moment().isAfter(moment(dueDate)) && status !== 'paid') {
    return 'overdue'
  }
  return status
}

const Expenses = ({companyId, placeId, selectedPlace}) => {
  const { t } = useTranslation()

  const permissions = usePermissions([{
    name: 'can_view_expenses',
    permission: 'places_can_view_expenses'
  }, {
    name: 'can_manage_expenses',
    permission: 'places_can_manage_expenses'
  }])

  const companyInfo = useSelector(state => state.company?.companyInfo) || {}
  const dateFormat = companyInfo.date_format ? companyInfo.date_format.replace('YY', 'YYYY') : 'DD-MM-YYYY'
  const currencySymbol = companyInfo?.currency_symbol || ''

  const expenseDialogRef = useRef(null)
  const confirmModalRef = useRef(null)

  const [companyStats, setCompanyStats] = useState({
    total_amount: 0,
    total_invoices: 0,
    total_places: 0,
    total_expenses_types: 0,
    places_expenses: [],
    type_expenses: [],
    material_expenses: [],
    supplier_expenses: []
  })

  const billingStatusOptions = [{id: 'paid', name: t('Paid')}, {id: 'draft', name: t('Draft')}, {id: 'billed', name: t('Billed')}, {id: 'overdue', name: t('Overdue')}]

  const [{currencies, expensesTypes, customers, suppliers, materials}, setData] = useState({currencies: [], expensesTypes: [], customers: [], suppliers: [], materials: []})
  const [{placeExpenses, loading}, setPlaceExpenses] = useState({loading: false, placeExpenses: []})
  const [sorting, setSorting] = useState([])
  const [query, setQuery] = useState('')
  const [selectedExpenseTypeIds, setSelectedExpenseTypeIds] = useState([])
  const [selectedBillingStatusIds, setSelectedBillingStatusIds] = useState([])
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([])
  const [filterDate, setFilterDate] = useState({start: moment().startOf('month'), end: moment().endOf('month')})

  const totalAmount = !placeId ? companyStats.total_amount : numeral(placeExpenses.reduce((acc, expense) => acc + Number(expense.amount || 0), 0)).format('0.00')
  const totalInvoices = !placeId ? companyStats.total_invoices : placeExpenses.length
  const totalExpensesTypes = !placeId ? companyStats.total_expenses_types : new Set(placeExpenses.filter(e => e.expenses_types_id).map(expense => expense.expenses_types_id)).size
  
  const totalPlaces = companyStats.total_places

  const customersMap = useMemo(() => {
    return customers.reduce((acc, customer) => {
      acc[customer.id] = customer.display_name || customer.name || '-'
      return acc
    }, {})
  }, [customers])

  const expenseTypesMap = useMemo(() => {
    return expensesTypes.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }, [expensesTypes])

  const filteredPlaceExpenses = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    return placeExpenses.filter(expense => {
      const expenseTypeId = String(expense.expenses_types_id || '')
      if (selectedExpenseTypeIds.length && !selectedExpenseTypeIds.some(id => String(id) === expenseTypeId)) {
        return false
      }

      const billingStatus = getBillingStatusText(expense.billing_status === 'sent' ? 'billed' : expense.billing_status, expense.invoice_item?.invoice?.due_date)
      
      if (selectedBillingStatusIds.length && !selectedBillingStatusIds.some(id => String(id) === billingStatus)) {
        return false
      }

      if (selectedSupplierIds.length && !selectedSupplierIds.some(id => String(id) === String(expense.supplier_id))) {
        return false
      }

      if (!normalizedQuery) {
        return true
      }

      const customerId = expense.customer_id ?? expense.cutomer_id
      const expenseType = expenseTypesMap[expense.expenses_types_id]
      const searchableValues = [
        expense.vendor_name,
        expense.invoice_number,
        expense.notes,
        expense.currency_code,
        expense.status,
        customersMap[customerId],
        expenseType?.name,
        formatExpenseDate(expense.invoice_date, dateFormat),
        formatExpenseAmount(expense.amount, expense.currency_code),
        expense.auto_detection ? t('AI detect') : t('Manual')
      ]

      return searchableValues.some(value => `${value || ''}`.toLowerCase().includes(normalizedQuery))
    })
  }, [customersMap, expenseTypesMap, placeExpenses, query, selectedExpenseTypeIds, selectedSupplierIds, selectedBillingStatusIds, t, dateFormat])

  const expensesMap = !placeId ? companyStats.type_expenses : Object.values(placeExpenses.reduce((acc, expense) => {
    const typeId = expense.expenses_types_id || 0
    const expenseType = expenseTypesMap[typeId]
    acc[typeId] = acc[typeId] || {total_amount: 0, expense_type_name: expenseType?.name || 'Unknown', color: expenseType?.color || '#bdbdbd'}
    acc[typeId].total_amount += Number(expense.amount || 0)
    return acc
  }, {}))

  useEffect(() => {
    init(companyId)
  }, [companyId])

  useEffect(() => {
    if (placeId) {
      getExpenses(placeId, filterDate)
    }
  }, [companyId, placeId, filterDate])

  useEffect(() => {
    if (!placeId) {
      getExpensesCompanyStats(companyId, {start_date: filterDate.start.format('YYYY-MM-DD'), end_date: filterDate.end.format('YYYY-MM-DD')}).then(res => {
        if (res) {
          setCompanyStats(res)
        }
      })
    }
  }, [placeId, filterDate, companyId])

  const init = async (companyId) => {
    const [expensesTypesRes, customersRes, currenciesRes, suppliersRes, materialsRes] = await Promise.all([
      getCompanyExpenseTypes(companyId),
      getCustomers(companyId),
      getCurrencies(),
      getSuppliers(companyId),
      getMaterials(companyId)
    ])
    if (expensesTypesRes) {
      setData(prev => ({...prev, expensesTypes: expensesTypesRes}))
    }
    if (currenciesRes) {
      setData(prev => ({...prev, currencies: currenciesRes}))
    }
    if (Array.isArray(customersRes)) {
      setData(prev => ({...prev, customers: customersRes}))
    }
    if (Array.isArray(suppliersRes)) {
      setData(prev => ({...prev, suppliers: suppliersRes}))
    }
    if (Array.isArray(materialsRes)) {
      setData(prev => ({...prev, materials: materialsRes}))
    }
  }

  const getExpenses = async (placeId, filterDate) => {
    setPlaceExpenses(prev => ({...prev, loading: true}))
    const res = await getPlaceExpenses(companyId, placeId, {
      start_date: filterDate.start.format('YYYY-MM-DD'),
      end_date: filterDate.end.format('YYYY-MM-DD')
    })
    if (Array.isArray(res)) {
      setPlaceExpenses({loading: false, placeExpenses: res})
      return
    }
    setPlaceExpenses(prev => ({...prev, loading: false}))
  }
  
  const handleChangeDate = ({startDate, endDate}) => {
    setFilterDate({start: moment(startDate), end: moment(endDate)})
  }

  const handleOpenCreateExpense = async () => {
    if (expenseDialogRef.current) {
      const { success } = await expenseDialogRef.current.show()
      if (success && placeId) {
        getExpenses(placeId, filterDate)
        init(companyId)
      }
    }
  }

  const handleEditExpense = async (expenseId) => {
    const expense = placeExpenses.find((item) => item.id === expenseId)
    if (!expense || !expenseDialogRef.current) {
      return
    }

    const { success } = await expenseDialogRef.current.show(expense)
    if (success && placeId) {
      getExpenses(placeId, filterDate)
      init(companyId)
    }
  }

  const handleViewExpense = (expense) => {
    if (!expense?.file) {
      return
    }

    window.open(expense.file, '_blank', 'noopener,noreferrer')
  }

  const handleDownloadExpense = (expense) => {
    downloadExpenseFile(companyId, placeId, expense.id)
  }

  const handleAskDeleteExpense = async (expense) => {
    const result = await confirmModalRef.current.show({title: t('This expense will be removed permanently.')})
    if (result.success) {
      const res = await deletePlaceExpense(companyId, placeId, expense.id)
      if (res) {
        getExpenses(placeId, filterDate)
      }
    }
  }

  const handleCreateSupplier = async ({name}) => {
    const res = await createSupplier(companyId, {name})
    if (res) {
      init(companyId)
    }
    return res
  }

  const handleCreateMaterial = async (data) => {
    const res = await createMaterial(companyId, data)
    if (res) {
      init(companyId)
    }
    return res
  }

  const handleExport = () => {
    const data = filteredPlaceExpenses.map(item => {
      return {
        type: expenseTypesMap[item.expenses_types_id],
        supplier: item.supplier || item.vendor_name || '-',
        billing_status: item.billing_status ? t(capitalize(item.billing_status)) : '-',
        customer: customersMap[item.customer_id ?? item.cutomer_id],
        materials: item.materials || [],
        amount: item.amount || '0.00',
        date: item.invoice_date,

      }
    })
    generateExpensesExcelExport(data, t, t('expenses-{{startDate}}-{{endDate}}', { startDate: filterDate.start.format('YYYY-MM-DD'), endDate: filterDate.end.format('YYYY-MM-DD') }))
  }

  const columns = useMemo(() => [
    columnHelper.accessor(row => getBillingStatusText(
      row.billing_status === 'sent' ? 'billed' : row.billing_status,
      row.invoice_item?.invoice?.due_date,
    ), {
      id: 'billingStatus',
      enableSorting: false,
      header: t('Billing status'),
      cell: ({row}) => (
        <InvoiceStatus
          status={row.original.billing_status === 'sent' ? 'billed' : row.original.billing_status}
          dueDate={row.original.invoice_item?.invoice?.due_date}
        />
      ),
    }),
    columnHelper.accessor(row => row.supplier?.name || '', {
      id: 'supplier',
      enableSorting: false,
      header: t('Supplier'),
      cell: ({row}) => (
        <div className={styles.vendorCell}>
          <div className={styles.primaryText}>{row.original.supplier?.name}</div>
          <div className={styles.secondaryText}>{row.original.invoice_number || '-'}</div>
        </div>
      ),
    }),
    columnHelper.accessor(row => customersMap[row.customer_id ?? row.cutomer_id] || 'N/A', {
      id: 'customer',
      enableSorting: false,
      header: t('Customer'),
      cell: ({getValue}) => <div className={styles.primaryText}>{getValue()}</div>,
    }),
    columnHelper.accessor(row => row.materials?.map(material => material.name || '-').join(', ') || '', {
      id: 'material',
      enableSorting: false,
      header: t('Material'),
      cell: ({row}) => (
        <div className={styles.materialValues}>
          {row.original.materials?.map((material, index) => (
            <div key={`${material.id || material.name}-${index}`} className={styles.primaryText}>
              {material.name || '-'}
            </div>
          ))}
        </div>
      ),
    }),
    columnHelper.accessor(row => row.materials?.map(material => Number(material.pivot?.quantity || 0)).join(', ') || '', {
      id: 'quantity',
      enableSorting: false,
      header: t('Quantity'),
      cell: ({row}) => (
        <div className={styles.materialValues}>
          {row.original.materials?.map((material, index) => (
            <div key={`${material.id || material.name}-${index}`} className={styles.primaryText}>
              {numeral(material.pivot?.quantity).format('0.[00]')}
            </div>
          ))}
        </div>
      ),
    }),
    columnHelper.accessor(row => row.materials?.map(material => Number(material.pivot?.price || 0)).join(', ') || '', {
      id: 'unitPrice',
      enableSorting: false,
      header: t('Unit price'),
      cell: ({row}) => (
        <div className={styles.materialValues}>
          {row.original.materials?.map((material, index) => (
            <div key={`${material.id || material.name}-${index}`} className={styles.primaryText}>
              {numeral(material.pivot?.price).format('0.[00]')}
            </div>
          ))}
        </div>
      ),
    }),
    columnHelper.accessor(row => Number(row.amount || 0), {
      id: 'amount',
      enableSorting: false,
      header: t('Amount'),
      cell: ({row}) => {
        return (
          <div className={cn(styles.primaryText, styles.row)}>
            {formatExpenseAmount(row.original.amount, row.original.currency_code)}
            {
              row.original.notes
                ? <div data-tooltip-content={row.original.notes} data-tooltip-id='note'>
                    <DescriptionIcon width={12} height={12} className={styles.noteIcon} />
                  </div>
                : null
            }
          </div>
        )
      },
    }),
    columnHelper.accessor(row => (row.invoice_date ? moment(row.invoice_date).valueOf() : 0), {
      id: 'date',
      enableSorting: false,
      header: t('Date'),
      cell: ({row}) => (
        <div className={styles.secondaryTextStrong}>
          {formatExpenseDate(row.original.invoice_date, dateFormat)}
        </div>
      ),
    }),
    columnHelper.accessor(row => expenseTypesMap[row.expenses_types_id]?.name || '', {
      id: 'type',
      enableSorting: false,
      header: t('Type'),
      cell: ({row}) => {
        const expenseType = expenseTypesMap[row.original.expenses_types_id]
        if (!expenseType) {
          return null
        }

        return (
          <div
            className={styles.typeBadge}
            style={{
              '--type-badge-color': expenseType.color || '#34C759',
              '--type-badge-background': `${expenseType.color || '#34C759'}14`
            }}
          >
            <span className={styles.typeBadgeDot} />
            <span>{expenseType.name}</span>
          </div>
        )
      },
    }),
    columnHelper.accessor(row => row.auto_detection ? t('AI detect') : t('Manual'), {
      id: 'detection',
      enableSorting: false,
      header: t('Detection'),
      cell: ({row}) => {
        const isAIDetect = row.original.auto_detection && row.original.has_file
        return (
          <div className={`${styles.detectionBadge} ${isAIDetect ? styles.auto : styles.manual}`}>
            {isAIDetect ? t('AI detect') : t('Manual')}
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'actions',
      enableSorting: false,
      header: '',
      cell: ({row}) => (
        <div className={styles.actionsCell}>
          <button
            type='button'
            className={styles.actionButton}
            onClick={() => handleViewExpense(row.original)}
            disabled={!row.original.file}
            aria-label={t('View expense file')}
          >
            <EyeIcon />
          </button>
          {
            permissions.can_manage_expenses
              ? <button
                  type='button'
                  className={styles.actionButton}
                  onClick={() => handleEditExpense(row.original.id)}
                  aria-label={t('Edit expense')}
                >
                  <EditIcon className={styles.editIcon} />
                </button>
              : null
          }
          <button
            type='button'
            className={styles.actionButton}
            onClick={() => handleDownloadExpense(row.original)}
            disabled={!row.original.file}
            aria-label={t('Download expense file')}
          >
            <DownloadIcon className={styles.downloadIcon} width={18} height={18} />
          </button>
          {
            permissions.can_manage_expenses
              ? <button
                  type='button'
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  onClick={() => handleAskDeleteExpense(row.original)}
                  aria-label={t('Delete expense')}
                >
                  <TrashIcon className={styles.trashIcon} />
                </button>
              : null
          }
        </div>
      ),
    }),
  ], [
    customersMap,
    dateFormat,
    expenseTypesMap,
    handleAskDeleteExpense,
    handleDownloadExpense,
    handleEditExpense,
    handleViewExpense,
    permissions.can_manage_expenses,
    t,
  ])

  const expensesTable = useReactTable({
    data: filteredPlaceExpenses,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => String(row.id),
    state: {sorting},
    onSortingChange: setSorting,
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{flex: 1}}>
          <div className={styles.headerTitle}>{placeId ? `${selectedPlace.name} - ` : ''} {t('Expenses')}</div>
          <div className={styles.headerDesc}>{placeId ? t('Place or project expense overview') : t('Global expense overview, select the project or place in order to see its own expenses')}</div>
        </div>
        <DRP
          right={true}
          definedRangesStyle={{right: 40}}
          initRange={{startDate: filterDate.start.toDate(), endDate: filterDate.end.toDate()}}
          onChange={handleChangeDate} />
        {
          placeId && permissions.can_manage_expenses
            ? <div>
                <Button variant="contained" color="primary" onClick={handleOpenCreateExpense} style={{height: 36}}>
                  { t('Add Expense') }
                </Button>
              </div>
            : null
        }
      </div>
      <div className={styles.stats}>
        <div className={styles.card}>
          <div className={styles.iconContainer}>
            <DollarIcon />
          </div>
          <div>
            <div className={styles.label}>
              { t('Total Expenses') }
            </div>
            <div className={styles.value}>
              { currencySymbol }{ totalAmount }
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.iconContainer}>
            <InvoiceIcon />
          </div>
          <div>
            <div className={styles.label}>
              { t('Total Invoices') }
            </div>
            <div className={styles.value}>
              { totalInvoices }
            </div>
          </div>
        </div>
        {
          placeId
            ? null
            : <div className={styles.card}>
                <div className={styles.iconContainer}>
                  <PlaceIcon style={{width: 17, height: 19}} color="#4080FC" />
                </div>
                <div>
                  <div className={styles.label}>
                    { t('Places') }
                  </div>
                  <div className={styles.value}>
                    { totalPlaces }
                  </div>
                </div>
              </div>
        }
        <div className={styles.card}>
          <div className={styles.iconContainer}>
            <ChartIcon />
          </div>
          <div>
            <div className={styles.label}>
              { t('Expense Types') }
            </div>
            <div className={styles.value}>
              { totalExpensesTypes }
            </div>
          </div>
        </div>
      </div>
      {
        !placeId || expensesMap.length
          ? <div className={`${styles.stats} ${styles.aggregateStats}`}>
              {
                !placeId
                  ? <div className={styles.card}>
                      <div className={styles.cardContent}>
                        <div className={styles.cardTitle}>{t('By Place')}</div>
                        <div>
                          {
                            companyStats.places_expenses.map((item, i) => {
                              return (
                                <div key={i} className={styles.row}>
                                  <div className={styles.cardRowTitle}>{item.place_name}</div>
                                  <div className={styles.cardRowValue}>{ currencySymbol }{item.total_amount}</div>
                                </div>
                              )
                            })
                          }
                        </div>
                      </div>
                    </div>
                  : null
              }
              {
                expensesMap.length
                  ? <div className={styles.card}>
                      <div className={styles.cardContent}>
                        <div className={styles.cardTitle}>{t('By Expense Type')}</div>
                        <div className={cn({[styles.placeExpensesByType]: placeId})}>
                          {
                            expensesMap.map((item, i) => {
                              return (
                                <div key={i} className={styles.row}>
                                  <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                    <div style={{width: 9, height: 9, borderRadius: '50%', backgroundColor: item.color, marginBottom: 0}} />
                                    <div className={styles.cardRowTitle}>{item.expense_type_name}</div>
                                  </div>
                                  {
                                    placeId ? <div style={{marginBottom: 2}}>&#x2022;</div> : null
                                  }
                                  <div className={styles.cardRowValue}>{ currencySymbol }{numeral(item.total_amount).format('0.00')}</div>
                                </div>
                              )
                            })
                          }
                        </div>
                      </div>
                    </div>
                  : null
              }
              {
                !placeId && companyStats.material_expenses?.length
                  ? <div className={styles.card}>
                      <div className={styles.cardContent}>
                        <div className={styles.cardTitle}>{t('By Materials')}</div>
                        <div>
                          {companyStats.material_expenses.map((item, i) => (
                            <div key={i} className={styles.row}>
                              <div className={styles.cardRowTitle}>{item.material_name}</div>
                              <div className={styles.cardRowValue}>{currencySymbol}{numeral(item.total_amount).format('0.00')}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  : null
              }
              {
                !placeId && companyStats.supplier_expenses?.length
                  ? <div className={styles.card}>
                      <div className={styles.cardContent}>
                        <div className={styles.cardTitle}>{t('By Suppliers')}</div>
                        <div>
                          {companyStats.supplier_expenses.map((item, i) => (
                            <div key={i} className={styles.row}>
                              <div className={styles.cardRowTitle}>{item.supplier_name}</div>
                              <div className={styles.cardRowValue}>{currencySymbol}{numeral(item.total_amount).format('0.00')}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  : null
              }
            </div>
          : null
      }
      {
        placeId
          ? <div>
              <div className={styles.tools}>
                <div className={styles.searchInputWrap}>
                  <Input
                    icon={<SearchIcon />}
                    placeholder={`${t('Search by customer name, file, invoice #, amount')}...`}
                    width="100%"
                    height='36px'
                    value={query}
                    onChange={(e) => setQuery(e.target.value)} />
                </div>
                <div className={styles.expenseTypeFilter}>
                  <InputSelectMultiple
                    id='expense-type-filter'
                    options={suppliers}
                    value={selectedSupplierIds}
                    valueKey='id'
                    labelKey='name'
                    onChange={(res) => {
                      setSelectedSupplierIds(res)
                    }}
                    placeholder={t('Filter by supplier')}
                  />
                </div>
                <div className={styles.expenseTypeFilter}>
                  <InputSelectMultiple
                    id='billing-status-filter'
                    options={billingStatusOptions}
                    value={selectedBillingStatusIds}
                    valueKey='id'
                    labelKey='name'
                    onChange={(res) => {
                      setSelectedBillingStatusIds(res)
                    }}
                    placeholder={t('Filter by billing status')}
                  />
                </div>
                <div className={styles.expenseTypeFilter}>
                  <InputSelectMultiple
                    id='expense-type-filter'
                    options={expensesTypes}
                    value={selectedExpenseTypeIds}
                    valueKey='id'
                    labelKey='name'
                    onChange={(res) => {
                      setSelectedExpenseTypeIds(res)
                    }}
                    placeholder={t('Filter by expense types')}
                  />
                </div>
                <button
                  type="button"
                  data-tooltip-id="excel"
                  data-tooltip-content={t('Export to Excel')}
                  onClick={handleExport}
                  className={styles.exportButton}>
                  <ExcelIcon />
                </button>
              </div>
              <div className={styles.table}>
                <Table
                  table={expensesTable}
                  columns={columns}
                  loading={loading}
                  placeholder={t('No expenses found')}
                  headerStyles={{padding: '8px'}}
                  tableClassName={styles.expensesDataTable}
                />
              </div>
            </div>
          : null
      }
      <CreateExpense
        companyId={companyId}
        placeId={placeId}
        currencies={currencies}
        expensesTypes={expensesTypes}
        customers={customers}
        suppliers={suppliers}
        materials={materials}
        onCreateSupplier={handleCreateSupplier}
        onCreateMaterial={handleCreateMaterial}
        ref={expenseDialogRef} />
      <ConfirmationModal ref={confirmModalRef} />
      <Tooltip
        id='note'
        effect='solid'
        variant='dark'
        className={styles.tooltip} />
      <Tooltip
        id='excel'
        effect='solid'
        place="top-start"
        className={cn(styles.tooltip, styles.excel)} />
    </div>
  )
}

export default Expenses
